import { Fragment, useMemo } from 'react';

import {
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  isVendorTaxonomyKey as isSearchableVendorTaxonomyKey,
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import H from '@zola/zola-ui/src/typography/Headings';
import { Heading } from '@zola/zola-ui/src/typography/Headings.styles';

import styled from '@emotion/styled';

import { isSearchLocation } from '~/util/searchUtils';
import TextUtils from '~/util/textUtils';

import { getCanonicalFacetDescription } from '../../utils/canonicalFacetHelpers';
import { TotalVendorsDescriptionProps } from '../TotalVendorsDescription/types';

const FormattedCount = styled(Heading)`
  display: inline;
`;

const MainTitle = styled(H.Title1)`
  display: inline;
`;

const FirstWord = styled.span`
  display: inline-block;

  &::first-letter {
    text-transform: lowercase;
  }
`;

const getCategoryHeading = (selectedTaxonomy: VendorTaxonomyKey | undefined) => {
  if (isSearchableVendorTaxonomyKey(selectedTaxonomy)) {
    return categoryHeadings(selectedTaxonomy);
  }
  return 'vendors';
};

const categoryHeadings = getVendorTaxonomySwitchFunc<SearchableVendorTaxonomyKey, string>({
  [BANDS_DJS_TAXONOMY_KEY]: 'bands and DJs',
  [BAR_SERVICES_TAXONOMY_KEY]: 'bar services & beverages',
  [CAKES_DESSERTS_TAXONOMY_KEY]: 'cakes & desserts',
  [CATERING_TAXONOMY_KEY]: 'caterers',
  [FLORISTS_TAXONOMY_KEY]: 'florists',
  [HAIR_MAKEUP_TAXONOMY_KEY]: 'beauty professionals',
  [PHOTOGRAPHERS_TAXONOMY_KEY]: 'photographers',
  [PLANNERS_TAXONOMY_KEY]: 'planners',
  [VENUES_TAXONOMY_KEY]: 'venues',
  [VIDEOGRAPHERS_TAXONOMY_KEY]: 'videographers',
});

export const PageTitle = ({
  presentation,
  totalHits,
  selectedTaxonomy,
  selectedLocation,
  selectedFacets,
  serif = false,
}: { presentation: 'h4' | 'h5'; serif?: boolean } & TotalVendorsDescriptionProps) => {
  const maxShown = selectedTaxonomy || selectedLocation ? 1000 : 10000;
  const numShown = Math.min(maxShown, totalHits);
  let formattedCount = TextUtils.formatWithCommas(numShown);

  if (numShown === maxShown) {
    formattedCount = `${formattedCount}+`;
  }

  const formattedTitle = useMemo(() => {
    const facetDescription = getCanonicalFacetDescription(selectedTaxonomy, selectedFacets) || '';
    const weddingPrefix = facetDescription ? 'wedding' : 'Wedding';
    const description = `${weddingPrefix} ${getCategoryHeading(selectedTaxonomy)}`;
    const location = isSearchLocation(selectedLocation) ? selectedLocation.label : undefined;
    let title = `${facetDescription} ${description}`.trim();
    if (location) {
      title = `${title} in ${location}`;
    }
    const words = title.split(' ');
    return (
      <Fragment>
        <FirstWord>{words[0]}</FirstWord> {words.slice(1).join(' ')}
      </Fragment>
    );
  }, [selectedFacets, selectedLocation, selectedTaxonomy]);

  return (
    <Fragment>
      <FormattedCount serif={serif} presentation={presentation} as="span" color="BLACK_100">
        {formattedCount}&nbsp;
      </FormattedCount>
      <MainTitle serif={serif} presentation={presentation}>
        {formattedTitle}
      </MainTitle>
    </Fragment>
  );
};
